import classNames from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { useDepartments } from "../../api/departments";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useTranslation } from "../../hooks/useTranslation";
import styles from "./UserSelect.module.css";

interface DepartmentSelectProps {
  selected?: string[];
  onChange: (departments: string[]) => void;
  label?: string;
  disabled?: boolean;
}

export function DepartmentSelect({ selected = [], onChange, label, disabled = false }: DepartmentSelectProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useClickOutside(rootRef, () => setIsOpen(false));

  const { data: allDepartments = [], isLoading } = useDepartments();

  const filtered = allDepartments.filter(
    (d) => d.toLowerCase().includes(search.toLowerCase()),
  );

  useLayoutEffect(() => {
    if (!inputWrapperRef.current || !dropdownRef.current) return;
    const inputRect = inputWrapperRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    setDropUp(window.innerHeight - inputRect.bottom < dropdownHeight);
  }, [isOpen, filtered.length]);

  function toggle(dept: string) {
    if (selected.includes(dept)) {
      onChange(selected.filter((d) => d !== dept));
    } else {
      onChange([...selected, dept]);
    }
  }

  return (
    <div className={styles["root"]} ref={rootRef}>
      {label && <span className={styles["label"]}>{label}</span>}

      {selected.length > 0 && (
        <div className={styles["selectedList"]}>
          {selected.map((dept) => (
            <span key={dept} className={styles["selectedItem"]}>
              {dept}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((d) => d !== dept))}
                  className={styles["removeButton"]}
                  aria-label={`Remove ${dept}`}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <div className={styles["inputWrapper"]} ref={inputWrapperRef}>
          <input
            type="text"
            placeholder={t("common.search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className={styles["searchInput"]}
          />
          {isOpen && (
            <div ref={dropdownRef} className={classNames(styles["dropdown"], dropUp && styles["dropdownAbove"])}>
              {isLoading ? (
                <div className={styles["loadingText"]}>{t("common.loading")}</div>
              ) : (
                <ul className={styles["results"]}>
                  {filtered.length === 0 && <li className={styles["emptyText"]}>{t("common.noData")}</li>}
                  {filtered.map((dept) => {
                    const isSelected = selected.includes(dept);
                    return (
                      <li key={dept} className={styles["resultItem"]}>
                        <button
                          type="button"
                          onClick={() => toggle(dept)}
                          className={classNames(styles["resultButton"], isSelected && styles["resultButtonSelected"])}
                        >
                          <span className={styles["resultName"]}>{dept}</span>
                          {isSelected && <span className={styles["check"]}>&#10003;</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
